import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Row,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  CloudUploadOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd';
import type {
  AnalyzeReceiptResult,
  AnalyzeSignal,
  MatrixDecision,
} from '@shared/api/hooks/useAnalyzeReceipt';
import { useAnalyzeReceipt } from '@shared/api/hooks/useAnalyzeReceipt';
import type { TrafficLight } from '@shared/api/hooks/useReceipts';

// ---------------------------------------------------------------------------
// Costanti visive
// ---------------------------------------------------------------------------

const LIGHT_LABEL: Record<TrafficLight, string> = {
  GREEN: 'Verde',
  YELLOW: 'Giallo',
  RED: 'Rosso',
};

const DECISION_COLOR: Record<MatrixDecision, string> = {
  AUTO_APPROVE: '#0A8F3F',
  REVIEW: '#C99A2A',
  REJECT: '#d42027',
  REJECT_FLAG: '#7A0000',
};

const DECISION_BG: Record<MatrixDecision, string> = {
  AUTO_APPROVE: '#e6f9ee',
  REVIEW: '#FFF5E8',
  REJECT: '#fde8e6',
  REJECT_FLAG: '#f5e0e0',
};

const DECISION_LABEL: Record<MatrixDecision, string> = {
  AUTO_APPROVE: 'APPROVAZIONE AUTOMATICA',
  REVIEW: 'REVISIONE MANUALE',
  REJECT: 'RIFIUTO',
  REJECT_FLAG: 'RIFIUTO + SEGNALAZIONE',
};

const DECISION_ICON: Record<MatrixDecision, React.ReactNode> = {
  AUTO_APPROVE: <SafetyCertificateOutlined />,
  REVIEW: <WarningOutlined />,
  REJECT: <WarningOutlined />,
  REJECT_FLAG: <WarningOutlined />,
};

// ---------------------------------------------------------------------------
// Dizionario codici segnale → spiegazione in italiano semplice
// ---------------------------------------------------------------------------

const SIGNAL_EXPLANATIONS: Record<string, string> = {
  EDITOR_SOFTWARE:
    "L'immagine è stata salvata da un software di fotoritocco o AI (es. Photoshop, GIMP, Canva), non direttamente da una fotocamera reale.",
  PHASH_REUSE:
    'Questa identica immagine risulta già caricata in precedenza. Potrebbe essere un tentativo di doppio incasso.',
  SCREEN_LIKE:
    "L'immagine sembra essere uno screenshot dello schermo e non una foto reale dello scontrino fisico.",
  INVALID_PARTITA_IVA:
    'Il numero di Partita IVA estratto non supera la validazione formale italiana (algoritmo Luhn). Potrebbe essere inventato o mal scritto.',
  NO_CAMERA_EXIF:
    "L'immagine non contiene i metadati tecnici tipici di una fotocamera (dati EXIF). Le foto reali quasi sempre li includono.",
  MODIFY_AFTER_CAPTURE:
    "I metadati indicano che l'immagine è stata modificata dopo la data di scatto originale — possibile segno di manipolazione.",
  C2PA_AI_DECLARED:
    "I metadati C2PA (standard di provenienza digitale) dichiarano esplicitamente che il contenuto è stato generato o modificato da un sistema AI.",
  FUTURE_DATE:
    'La data indicata sullo scontrino è nel futuro rispetto a oggi — non credibile per uno scontrino reale.',
  DUPLICATE:
    'Scontrino già caricato in precedenza da questo o da un altro utente. Caricamento duplicato.',
  ASPECT_RATIO:
    "Le proporzioni dell'immagine non corrispondono a quelle tipiche di uno scontrino fisico — potrebbe essere un artefatto generato.",
  EXIF_MISMATCH:
    "I metadati EXIF non sono coerenti tra loro (es. data GPS vs data scatto) — possibile segno di manipolazione manuale dei metadati.",
  LOW_CONFIDENCE:
    "Il sistema ha letto i dati con bassa affidabilità. Lo scontrino potrebbe essere illeggibile, troncato o costruito artificialmente.",
  METADATA_STRIPPED:
    "I metadati dell'immagine sono stati rimossi. Un'operazione normale per le fotocamere, ma insolita se altri segnali sono presenti.",
  SUSPICIOUSLY_CLEAN:
    "L'immagine appare troppo 'pulita' e priva delle imperfezioni fisiche tipiche di una foto reale (ombre, pieghe, granulazione).",
  REUSED_RECEIPT_NUMBER:
    'Il numero di scontrino è già associato a un altro scontrino approvato in precedenza.',
  INVALID_DATE_FORMAT:
    'La data estratta ha un formato non valido o non riconoscibile come data di acquisto reale.',
  FONT_INCONSISTENCY:
    "Le font o la tipografia dello scontrino mostrano incongruenze — possibile ricostruzione digitale dell'immagine.",
  COLOR_ANOMALY:
    "Lo scontrino mostra colori o sfumature anomali rispetto a una stampa termica reale (bianco/nero).",
};

/** Ritorna la spiegazione per un codice segnale, con fallback generico. */
function getSignalExplanation(code: string): string {
  return (
    SIGNAL_EXPLANATIONS[code] ??
    `Segnale tecnico rilevato (codice: ${code}). Verificare manualmente il dettaglio.`
  );
}

// ---------------------------------------------------------------------------
// Sottocomponenti
// ---------------------------------------------------------------------------

/** Grande badge semaforo con etichetta, confidence/score e dimensione configurabile. */
function TrafficLightBig({
  light,
  label,
  score,
  scoreLabel,
}: {
  light: TrafficLight;
  label: string;
  score: number;
  scoreLabel: string;
}): React.ReactElement {
  const dotColor = light === 'GREEN' ? '#0A8F3F' : light === 'YELLOW' ? '#C99A2A' : '#d42027';
  return (
    <div
      style={{
        background: '#fff',
        border: `2px solid ${dotColor}`,
        borderRadius: 10,
        padding: '16px 24px',
        textAlign: 'center',
        minWidth: 160,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: '#7a756c', letterSpacing: 0.5, marginBottom: 8 }}>
        {label}
      </div>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: dotColor + '18',
          border: `1px solid ${dotColor}`,
          borderRadius: 6,
          padding: '4px 12px',
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: dotColor,
            flexShrink: 0,
            display: 'inline-block',
          }}
        />
        <span style={{ fontSize: 14, fontWeight: 900, color: dotColor, letterSpacing: 0.5 }}>
          {LIGHT_LABEL[light]}
        </span>
      </div>
      <div style={{ marginTop: 8, fontSize: 22, fontWeight: 900, color: dotColor, lineHeight: 1 }}>
        {score}
        <span style={{ fontSize: 13, fontWeight: 600, color: '#7a756c' }}> {scoreLabel}</span>
      </div>
    </div>
  );
}

/** Chip per il tipo di segnale (hard/soft). */
function SignalKindChip({ kind }: { kind: AnalyzeSignal['kind'] }): React.ReactElement {
  return kind === 'hard' ? (
    <Tag color="error" style={{ fontWeight: 800, letterSpacing: 0.3 }}>
      PROVA FORTE
    </Tag>
  ) : (
    <Tag color="warning" style={{ fontWeight: 700 }}>
      indizio debole
    </Tag>
  );
}

/** Lista segnali antifrode con spiegazioni didattiche in italiano. */
function SignalsList({ signals }: { signals: AnalyzeSignal[] }): React.ReactElement {
  if (signals.length === 0) {
    return (
      <Alert
        type="success"
        showIcon
        message="Nessun segnale di manomissione rilevato"
        description="L'analisi non ha trovato anomalie tecniche nell'immagine. Questo non esclude la possibilità di scontrini falsi con tecniche non ancora coperte."
      />
    );
  }

  // Ordina: hard prima, poi per peso decrescente
  const sorted = [...signals].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'hard' ? -1 : 1;
    return b.weight - a.weight;
  });

  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      {sorted.map((sig) => (
        <div
          key={sig.code}
          style={{
            background: sig.kind === 'hard' ? '#fde8e6' : '#fff5e8',
            border: `1px solid ${sig.kind === 'hard' ? '#f5c6c6' : '#f5dbb0'}`,
            borderRadius: 8,
            padding: '12px 16px',
          }}
        >
          <Row gutter={12} align="top" wrap={false}>
            <Col flex="none">
              <SignalKindChip kind={sig.kind} />
            </Col>
            <Col flex="auto">
              <Space size={6} wrap>
                <Typography.Text strong style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {sig.code}
                </Typography.Text>
                <Tooltip title="Peso del segnale sulla valutazione finale">
                  <Tag
                    color={sig.weight < 0 ? 'red' : 'default'}
                    style={{ fontFamily: 'monospace' }}
                  >
                    {sig.weight < 0 ? sig.weight : `+${sig.weight}`}
                  </Tag>
                </Tooltip>
              </Space>
              {/* Spiegazione in italiano semplice */}
              <div
                style={{
                  marginTop: 4,
                  fontSize: 13,
                  fontWeight: 600,
                  color: sig.kind === 'hard' ? '#7A0000' : '#7a5000',
                }}
              >
                {getSignalExplanation(sig.code)}
              </div>
              {/* Dettaglio tecnico */}
              {sig.detail ? (
                <div style={{ marginTop: 4, fontSize: 11, color: '#7a756c', fontFamily: 'monospace' }}>
                  Dettaglio tecnico: {sig.detail}
                </div>
              ) : null}
            </Col>
          </Row>
        </div>
      ))}
    </Space>
  );
}

/** Pannello "Campi estratti" con le letture dello scontrino. */
function ExtractedFields({
  fields,
  confidence,
}: {
  fields: AnalyzeReceiptResult['reading']['fields'];
  confidence: number;
}): React.ReactElement {
  const pct = Math.round(confidence * 100);
  return (
    <Descriptions column={2} size="small" bordered>
      <Descriptions.Item label="Esercente">{fields.storeName ?? '—'}</Descriptions.Item>
      <Descriptions.Item label="Ragione sociale">{fields.businessName ?? '—'}</Descriptions.Item>
      <Descriptions.Item label="P.IVA">{fields.partitaIva ?? '—'}</Descriptions.Item>
      <Descriptions.Item label="N° scontrino">{fields.receiptNumber ?? '—'}</Descriptions.Item>
      <Descriptions.Item label="Data">{fields.date ?? '—'}</Descriptions.Item>
      <Descriptions.Item label="Importo totale">
        {fields.totalAmount != null ? `€ ${fields.totalAmount.toFixed(2)}` : '—'}
      </Descriptions.Item>
      <Descriptions.Item label="Confidence lettura" span={2}>
        <Tag color={pct >= 85 ? 'green' : pct >= 50 ? 'orange' : 'red'}>{pct}%</Tag>
      </Descriptions.Item>
    </Descriptions>
  );
}

/** Blocco "Partner abbinato". */
function SponsorInfo({ sponsor }: { sponsor: AnalyzeReceiptResult['sponsor'] }): React.ReactElement {
  if (!sponsor.matched) {
    return (
      <Alert
        type="warning"
        message="Nessun partner abbinato"
        description="La P.IVA o la ragione sociale non corrisponde ad alcun partner registrato. Lo scontrino non darebbe punti."
        showIcon
      />
    );
  }
  return (
    <Alert
      type="success"
      message={`Partner: ${sponsor.name ?? '—'}`}
      description={`Abbinato tramite: ${sponsor.matchedBy ?? '—'}`}
      showIcon
    />
  );
}

// ---------------------------------------------------------------------------
// Pagina principale
// ---------------------------------------------------------------------------

export function ReceiptInspectorPage(): React.ReactElement {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [result, setResult] = useState<AnalyzeReceiptResult | null>(null);

  const mutation = useAnalyzeReceipt();

  // Gestione selezione file — previene upload automatico, salva in state.
  const handleBeforeUpload = (f: File): boolean => {
    // Revoca il vecchio object URL per non perdere memoria
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
    mutation.reset();
    return false; // Ant Design non deve fare l'upload da solo
  };

  const handleRemove = (): boolean => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setResult(null);
    mutation.reset();
    return true;
  };

  const handleAnalyze = async (): Promise<void> => {
    if (!file) return;
    try {
      const data = await mutation.mutateAsync(file);
      setResult(data);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore durante l\'analisi'));
    }
  };

  const handleReset = (): void => {
    handleRemove();
  };

  // Converte bytes in stringa leggibile
  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      {/* ------------------------------------------------------------------ */}
      {/* Intestazione pagina                                                  */}
      {/* ------------------------------------------------------------------ */}
      <div style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Verifica Scontrino
        </Typography.Title>
        <Typography.Text type="secondary">
          Tool antifrode · Analisi visiva e metadati dell&apos;immagine. Doppio uso: training e pre-verifica.
          Non crea scontrini reali.
        </Typography.Text>
      </div>

      <Row gutter={[24, 24]} align="top">
        {/* ---------------------------------------------------------------- */}
        {/* Colonna sinistra — Upload + preview immagine                      */}
        {/* ---------------------------------------------------------------- */}
        <Col xs={24} lg={result ? 10 : 24}>
          <Card title="Carica immagine" size="small">
            <Upload.Dragger
              name="file"
              accept="image/*"
              maxCount={1}
              showUploadList={file ? { showRemoveIcon: true } : false}
              fileList={
                file
                  ? ([
                      {
                        uid: '-1',
                        name: file.name,
                        status: 'done',
                        size: file.size,
                        type: file.type,
                      } as UploadFile,
                    ])
                  : []
              }
              beforeUpload={handleBeforeUpload}
              onRemove={handleRemove}
              disabled={mutation.isPending}
              style={{ borderRadius: 8 }}
            >
              <p style={{ margin: '12px 0 4px' }}>
                <CloudUploadOutlined style={{ fontSize: 36, color: '#AC0000' }} />
              </p>
              <p style={{ fontWeight: 700, margin: 0 }}>
                Trascina qui l&apos;immagine o clicca per selezionarla
              </p>
              <p style={{ color: '#7a756c', fontSize: 12, margin: '4px 0 12px' }}>
                Formati accettati: JPEG, PNG, WebP, HEIC · Max 10 MB
              </p>
            </Upload.Dragger>

            {/* Pulsante Analizza */}
            {file && !result ? (
              <Button
                type="primary"
                block
                size="large"
                style={{ marginTop: 16, background: '#AC0000', borderColor: '#AC0000' }}
                onClick={handleAnalyze}
                loading={mutation.isPending}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Analisi in corso (Vision AI)…' : 'Analizza'}
              </Button>
            ) : null}

            {/* Pulsante reset dopo risultato */}
            {result ? (
              <Button
                block
                size="large"
                icon={<ReloadOutlined />}
                style={{ marginTop: 16 }}
                onClick={handleReset}
              >
                Analizza un altro
              </Button>
            ) : null}
          </Card>

          {/* Anteprima immagine */}
          {previewUrl ? (
            <Card size="small" style={{ marginTop: 16 }}>
              <img
                src={previewUrl}
                alt="Anteprima scontrino"
                style={{
                  width: '100%',
                  maxHeight: 480,
                  objectFit: 'contain',
                  border: '1px solid #e4e1d8',
                  borderRadius: 6,
                  display: 'block',
                }}
              />
            </Card>
          ) : null}
        </Col>

        {/* ---------------------------------------------------------------- */}
        {/* Colonna destra — Risultati analisi                               */}
        {/* ---------------------------------------------------------------- */}
        {mutation.isPending ? (
          <Col xs={24} lg={14}>
            <Card>
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16, color: '#7a756c', fontWeight: 600 }}>
                  Analisi in corso — Vision AI in elaborazione…
                </div>
                <div style={{ fontSize: 12, color: '#a8a39a', marginTop: 6 }}>
                  L&apos;operazione può richiedere alcuni secondi.
                </div>
              </div>
            </Card>
          </Col>
        ) : null}

        {result && !mutation.isPending ? (
          <Col xs={24} lg={14}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>

              {/* ----------------------------------------------------------
                  PANNELLO VERDETTO — scanner style
               ---------------------------------------------------------- */}
              <div
                style={{
                  background: DECISION_BG[result.matrixDecision],
                  border: `2px solid ${DECISION_COLOR[result.matrixDecision]}`,
                  borderRadius: 12,
                  padding: '20px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 36,
                    color: DECISION_COLOR[result.matrixDecision],
                    flexShrink: 0,
                  }}
                >
                  {DECISION_ICON[result.matrixDecision]}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 900,
                      letterSpacing: 2,
                      color: DECISION_COLOR[result.matrixDecision],
                      textTransform: 'uppercase',
                    }}
                  >
                    Verdetto finale
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 900,
                      color: DECISION_COLOR[result.matrixDecision],
                      letterSpacing: -0.5,
                      lineHeight: 1.2,
                    }}
                  >
                    {DECISION_LABEL[result.matrixDecision]}
                  </div>
                  <div style={{ fontSize: 12, color: '#7a756c', marginTop: 4 }}>
                    Disparità R-V: <strong>{result.disparity.toFixed(1)}</strong>
                    {result.authenticity.hardFail ? (
                      <Tag color="error" style={{ marginLeft: 8 }}>
                        Hard fail
                      </Tag>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Alert scontrino artefatto (R alto + V basso) */}
              {result.isDisparityFlag ? (
                <Alert
                  type="error"
                  showIcon
                  message="Sospetto scontrino artefatto"
                  description={`Lettura alta ma veridicità bassa: R ${Math.round(result.reading.confidence * 100)}%, V ${result.authenticity.score}/100 (disparità: ${result.disparity.toFixed(1)}). Questo pattern è tipico degli scontrini generati digitalmente: leggibili ma privi di autenticità fisica.`}
                  style={{ border: '1px solid #d42027' }}
                />
              ) : null}

              {/* Alert duplicato */}
              {result.duplicate.found ? (
                <Alert
                  type="warning"
                  showIcon
                  message="Immagine già caricata"
                  description={`Questa immagine è già presente nel sistema (receipt ID: ${result.duplicate.receiptId ?? '—'}). Potenziale tentativo di doppio incasso.`}
                />
              ) : null}

              {/* ----------------------------------------------------------
                  DOPPIO SEMAFORO
               ---------------------------------------------------------- */}
              <Card
                title="Semafori antifrode"
                size="small"
                headStyle={{ background: '#fafafa' }}
              >
                <Row gutter={16} justify="center">
                  <Col>
                    <TrafficLightBig
                      light={result.reading.light}
                      label="Lettura dati"
                      score={Math.round(result.reading.confidence * 100)}
                      scoreLabel="/ 100"
                    />
                  </Col>
                  <Col>
                    <TrafficLightBig
                      light={result.authenticity.light}
                      label="Veridicità"
                      score={result.authenticity.score}
                      scoreLabel="/ 100"
                    />
                  </Col>
                </Row>
                <div
                  style={{
                    marginTop: 16,
                    textAlign: 'center',
                    fontSize: 12,
                    color: '#7a756c',
                  }}
                >
                  Disparità (R − V) ={' '}
                  <strong
                    style={{
                      color: result.disparity > 30 ? '#d42027' : '#0A8F3F',
                    }}
                  >
                    {result.disparity.toFixed(1)}
                  </strong>
                  <Tooltip title="Una disparità alta (R alto, V basso) indica uno scontrino probabilmente artefatto: leggibile ma non autentico.">
                    <Typography.Text
                      type="secondary"
                      style={{ marginLeft: 6, cursor: 'help', fontSize: 11 }}
                    >
                      (?)
                    </Typography.Text>
                  </Tooltip>
                </div>
              </Card>

              {/* ----------------------------------------------------------
                  SEGNALI — cuore del tool training
               ---------------------------------------------------------- */}
              <Card
                title={
                  <Space>
                    Segnali di veridicità
                    <Tag
                      color={result.authenticity.signals.length === 0 ? 'green' : 'default'}
                    >
                      {result.authenticity.signals.length === 0
                        ? 'nessuno'
                        : `${result.authenticity.signals.length} rilevati`}
                    </Tag>
                    {result.authenticity.hasHard ? (
                      <Tag color="error">Hard fail presente</Tag>
                    ) : null}
                  </Space>
                }
                size="small"
                headStyle={{ background: '#fafafa' }}
              >
                <SignalsList signals={result.authenticity.signals} />
              </Card>

              {/* ----------------------------------------------------------
                  CAMPI ESTRATTI
               ---------------------------------------------------------- */}
              <Card
                title="Dati estratti dallo scontrino"
                size="small"
                headStyle={{ background: '#fafafa' }}
              >
                <ExtractedFields
                  fields={result.reading.fields}
                  confidence={result.reading.confidence}
                />
              </Card>

              {/* ----------------------------------------------------------
                  PARTNER ABBINATO
               ---------------------------------------------------------- */}
              <Card title="Partner abbinato" size="small" headStyle={{ background: '#fafafa' }}>
                <SponsorInfo sponsor={result.sponsor} />
              </Card>

              {/* ----------------------------------------------------------
                  PANNELLO TECNICO — collassabile
               ---------------------------------------------------------- */}
              <Collapse
                ghost
                items={[
                  {
                    key: 'tech',
                    label: (
                      <Typography.Text strong style={{ fontSize: 12, color: '#7a756c' }}>
                        Dettagli tecnici (EXIF · C2PA · metadati)
                      </Typography.Text>
                    ),
                    children: (
                      <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        {/* Info immagine */}
                        <Descriptions title="Immagine" size="small" column={2} bordered>
                          <Descriptions.Item label="Formato">
                            {result.imageInfo.format}
                          </Descriptions.Item>
                          <Descriptions.Item label="Dimensioni">
                            {result.imageInfo.width} × {result.imageInfo.height} px
                          </Descriptions.Item>
                          <Descriptions.Item label="Peso">
                            {formatBytes(result.imageInfo.sizeBytes)}
                          </Descriptions.Item>
                          <Descriptions.Item label="Sorgente">
                            {result.metadata.source}
                          </Descriptions.Item>
                          <Descriptions.Item label="Screen-like" span={2}>
                            <Tag color={result.metadata.screenLike ? 'error' : 'success'}>
                              {result.metadata.screenLike ? 'SÌ' : 'No'}
                            </Tag>
                          </Descriptions.Item>
                        </Descriptions>

                        {/* EXIF */}
                        <div>
                          <Typography.Text strong style={{ fontSize: 12 }}>
                            EXIF
                          </Typography.Text>
                          {Object.keys(result.metadata.exif).length === 0 ? (
                            <Alert
                              type="warning"
                              message="Nessun dato EXIF presente"
                              style={{ marginTop: 8 }}
                            />
                          ) : (
                            <pre
                              style={{
                                marginTop: 8,
                                background: '#f5f5f0',
                                border: '1px solid #e4e1d8',
                                borderRadius: 6,
                                padding: 12,
                                fontSize: 11,
                                overflowX: 'auto',
                                maxHeight: 200,
                              }}
                            >
                              {JSON.stringify(result.metadata.exif, null, 2)}
                            </pre>
                          )}
                        </div>

                        {/* C2PA */}
                        <div>
                          <Typography.Text strong style={{ fontSize: 12 }}>
                            C2PA (provenienza digitale)
                          </Typography.Text>
                          {Object.keys(result.metadata.c2pa).length === 0 ? (
                            <Alert
                              type="info"
                              message="Nessun dato C2PA presente"
                              description="Il file non contiene dichiarazioni C2PA di provenienza."
                              style={{ marginTop: 8 }}
                            />
                          ) : (
                            <pre
                              style={{
                                marginTop: 8,
                                background: '#f5f5f0',
                                border: '1px solid #e4e1d8',
                                borderRadius: 6,
                                padding: 12,
                                fontSize: 11,
                                overflowX: 'auto',
                                maxHeight: 200,
                              }}
                            >
                              {JSON.stringify(result.metadata.c2pa, null, 2)}
                            </pre>
                          )}
                        </div>
                      </Space>
                    ),
                  },
                ]}
              />
            </Space>
          </Col>
        ) : null}
      </Row>
    </div>
  );
}
